import React from "react";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import theme from "./theme/theme1";
import {
  Facebook as FacebookIcon,
  Linkedin as LinkedInIcon,
  Twitter as TwitterIcon,
  Youtube as YouTubeIcon,
  Instagram as InstagramIcon,
} from "lucide-react";

const InstitutionFooter = () => {

  // Parse the JSON string into a JavaScript object
  const companyDetails = useSelector((state) => state.companyReducer.company);
  return (
    <footer
      className="font-serif tracking-wide relative overflow-hidden"
      style={{ backgroundColor: theme.footerBg, fontFamily: theme.fontFamily }}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 lg:gap-12 gap-8 py-14 px-12 relative z-20">
        {["Company", "Products", "Resources", "Connect"].map((sectionTitle, index) => (
          <div key={sectionTitle}>
            <motion.h2
              className="text-lg font-semibold mb-6"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{ color: theme.titleText }}
            >
              {sectionTitle}
            </motion.h2>
            <ul className="space-y-5">
              {["About", "Services", "Contact"].map((linkText) => (
                <li key={linkText}>
                  <button
                    type="button"
                    className="text-base flex items-center transition-all"
                    style={{ color: theme.footerLinkText }}
                    onMouseOver={(e) => (e.currentTarget.style.color = theme.footerLinkHoverText)}
                    onMouseOut={(e) => (e.currentTarget.style.color = theme.footerLinkText)}
                  >
                    {linkText}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <hr style={{ borderColor: theme.footerDivider }} />

      <div className="my-8 px-12 flex flex-wrap sm:justify-between gap-6 relative z-20">
        {/* Social Media Links */}
        <div>
          <h6 className="font-bold text-lg mb-2 text-gray-800">Follow Us</h6>
          <div className="flex space-x-2">
            {companyDetails?.socialMediaAccounts?.map((account, index) => {
              const lowerCaseName = account.name?.toLowerCase();
              let IconComponent;

              // Dynamically map social media name to icon
              switch (lowerCaseName) {
                case "twitter":
                  IconComponent = TwitterIcon;
                  break;
                case "linkedin":
                  IconComponent = LinkedInIcon;
                  break;
                case "youtube":
                  IconComponent = YouTubeIcon;
                  break;
                case "facebook":
                  IconComponent = FacebookIcon;
                  break;
                case "instagram":
                  IconComponent = InstagramIcon;
                  break;
                default:
                  return null; // Skip unsupported accounts
              }

              // Skip rendering if the account does not have a valid URL
              if (!account.value) return null;

              return (
                <a
                  key={index}
                  href={account.value}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`hover:bg-gray-100 rounded-full p-2 transition ${lowerCaseName === "twitter"
                    ? "text-blue-500"
                    : lowerCaseName === "linkedin"
                      ? "text-blue-700"
                      : lowerCaseName === "youtube"
                        ? "text-red-600"
                        : lowerCaseName === "facebook"
                          ? "text-blue-800"
                          : "text-pink-500"
                    }`}
                >
                  <IconComponent className="h-8 w-8" aria-hidden="true" />
                </a>
              );
            })}
          </div>
        </div>


        <p className="text-sm" style={{ color: theme.titleText }}>
          © 2024 Your Institution. All rights reserved.
        </p>
      </div>

      <img
        src={theme.bgImage}
        alt=""
        aria-hidden="true"
        className="absolute w-full inset-0 object-cover opacity-5 -z-0"
      />
    </footer>
  );
};

export default InstitutionFooter;
